import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { suggestVideos } from '../../services/videoApi';
import { logoutApi } from '../../services/authApi';
import { SessionManager } from '../../utils/SessionManager';
import { useSnackbar } from '../../contexts/SnackbarProvider';
import { SnackbarType } from '../Snackbar';
import { HeaderState, SearchSuggestion } from './types';
import { useSuggestions } from '../../hooks/useSuggestions';
import { useIsMobile } from '../../config/breakpoints';

import { User } from '../../types/entities';

export const useHeader = (currentUser?: User) => {
  const isMobile = useIsMobile();
  const initialState: HeaderState = {
    menuOpen: false,
    searchQuery: '',
    isMobile: isMobile,
    searchBarVisible: true,
    searchBarCompact: false,
    lastScrollY: 0,
    isModalOpen: false,
    hasUnreadNotifications: false,
    isNotificationPanelOpen: false,
  };

  const [state, setState] = useState<HeaderState>(initialState);

  const updateState = (newState: Partial<HeaderState>) => {
    setState(prev => ({ ...prev, ...newState }));
  };

  // サジェスト機能を共通フックで管理
  const suggestionHook = useSuggestions({
    apiFunction: suggestVideos,
    testIdPrefix: 'header__suggestion',
    onSuggestionClick: (suggestion) => {
      const value = suggestion.value || '';
      updateState({ searchQuery: value });
      navigate(`/?q=${encodeURIComponent(value)}`);
    }
  });

  const menuRef = useRef<HTMLDivElement>(null);
  const navigateOriginal = useNavigate();
  const navigate = (path: string, options?: any) => {
    return navigateOriginal(path, options);
  };
  const [searchParams] = useSearchParams();
  const showSnackbar = useSnackbar();

  // 初期化時に検索クエリをURLから取得
  useEffect(() => {
    const q = searchParams.get('q') || '';
    updateState({ searchQuery: q });
  }, [searchParams]);

  // 検索クエリが変更されたときにサジェストフックを更新
  useEffect(() => {
    suggestionHook.updateQuery(state.searchQuery);
  }, [state.searchQuery, suggestionHook]);

  // 通知状態変更イベントのリスナーを設定
  useEffect(() => {
    const handleNotificationChange = (event: CustomEvent) => {
      updateState({ hasUnreadNotifications: event.detail.hasUnread });
    };

    window.addEventListener('notificationStateChange', handleNotificationChange as EventListener);
    
    return () => {
      window.removeEventListener('notificationStateChange', handleNotificationChange as EventListener);
    };
  }, []);

  // モバイル判定の更新は useIsMobile フックで管理されるため削除

  // スクロール位置に基づくヘッダーの表示/非表示制御
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const scrollingDown = currentScrollY > state.lastScrollY;
          const scrollingUp = currentScrollY < state.lastScrollY;

          if (scrollingDown && currentScrollY > 100) {
            updateState({
              searchBarVisible: false,
              searchBarCompact: true,
              lastScrollY: currentScrollY
            });
          } else if (scrollingUp || currentScrollY <= 100) {
            updateState({
              searchBarVisible: true,
              searchBarCompact: false,
              lastScrollY: currentScrollY
            });
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [state.lastScrollY]);

  // メニュー外クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // メニューのトリガー要素内のクリックかチェック
      if (menuRef.current && menuRef.current.contains(target)) {
        return;
      }
      
      // Portal内のドロップダウンメニュー要素かチェック
      const dropdownMenu = document.querySelector('.dropdown-menu-portal');
      if (dropdownMenu && dropdownMenu.contains(target)) {
        return;
      }
      
      // 上記のどちらでもない場合はメニューを閉じる
      updateState({ menuOpen: false });
    };

    if (state.menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [state.menuOpen]);

  const handleLogout = async () => {
    try {
      // メニューを閉じてからログアウト処理
      updateState({ menuOpen: false });
      await logoutApi();
      SessionManager.clearSession();
             showSnackbar('ログアウトしました', SnackbarType.SUCCESS);
       navigate('/login');
     } catch (error) {
       console.error('Logout error:', error);
       showSnackbar('ログアウトに失敗しました', SnackbarType.ERROR);
    }
  };

  const handleAccount = () => {
    // メニューを閉じてから画面遷移
    updateState({ menuOpen: false });
    navigate('/account');
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  const handleNotificationToggle = (isOpen: boolean) => {
    updateState({ isNotificationPanelOpen: isOpen });
  };

  const toggleMenu = () => {
    const newMenuOpen = !state.menuOpen;
    updateState({ menuOpen: newMenuOpen });
    
    // メニューが開いているときはスクロールを無効にする
    if (typeof document !== 'undefined') {
      if (newMenuOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = '';
      }
    }
  };

  // 検索フォーム送信時：入力があれば ?q=xxx へ遷移、空ならルートへ戻る
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = state.searchQuery.trim();
    if (trimmed) {
      navigate(`/?q=${encodeURIComponent(trimmed)}`);
    } else {
      navigate('/');
    }
    // サジェスト非表示
    suggestionHook.setIsInputFocused(false);
  };

  const handleAdminNotifications = useCallback(() => {
    // メニューを閉じてから画面遷移
    updateState({ menuOpen: false });
    navigate('/admin/notifications');
  }, [navigate, updateState]);

  // 検索クエリを更新する関数
  const handleSearchQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateState({ searchQuery: e.target.value });
  };

  return {
    state,
    suggestions: suggestionHook.suggestions,
    isInputFocused: suggestionHook.isInputFocused,
    selectedSuggestionIndex: suggestionHook.selectedSuggestionIndex,
    menuRef,
    handleLogout,
    handleAccount,
    handleLogoClick,
    handleNotificationToggle,
    toggleMenu,
    handleSearchSubmit,
    handleBlur: suggestionHook.handleBlur,
    handleKeyDown: suggestionHook.handleKeyDown,
    handleSuggestionKeyDown: suggestionHook.handleSuggestionKeyDown,
    handleSuggestionClick: suggestionHook.handleSuggestionClick,
    handleFocus: suggestionHook.handleFocus,
    handleAdminNotifications,
    handleSearchQueryChange,
  };
}; 
